import * as THREE from 'three';
import { state } from './state.js';
import { applyStyle, updateZSpacing, applyPavement, updatePavementRepeat } from './scene-setup.js';
import {
  setCameraType,
  setCameraMode,
  setCameraZoom,
  setCameraHeight,
  setCameraPanX,
  applyCameraPreset,
  setFloorHeight,
  getActiveCamera
} from './camera-controls.js';
import { spawnAvatar } from './avatar-controller.js';
import {
  openExplorerModal,
  closeExplorerModal,
  toggleExplorerModal,
  openSaveModal,
  closeSaveModal,
  confirmSaveCurrentScene,
  closeDeleteConfirm,
  executeDeleteProject,
  captureSceneThumbnail,
  resetToDefaultScene,
  renderProjectGrid,
  showToast,
  slugify,
  initRecoveryKeyUi,
  updateRecoveryUi
} from './project-io.js';

/**
 * Gắn kết toàn bộ sự kiện DOM UI cho Multiplane 2.5D Studio
 */
export function initUI() {
  // 1. Mouse & Touch Parallax
  window.addEventListener('mousemove', (e) => {
    state.mouse.targetX = (e.clientX / window.innerWidth) * 2 - 1;
    state.mouse.targetY = -(e.clientY / window.innerHeight) * 2 + 1;
  });

  window.addEventListener(
    'touchmove',
    (e) => {
      if (e.touches.length > 0 && state.currentCameraMode === 'parallax') {
        const touch = e.touches[0];
        state.mouse.targetX = (touch.clientX / window.innerWidth) * 2 - 1;
        state.mouse.targetY = -(touch.clientY / window.innerHeight) * 2 + 1;
      }
    },
    { passive: true }
  );

  // 2. Chọn Style
  document.querySelectorAll('.style-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const styleKey = btn.getAttribute('data-style');
      applyStyle(styleKey);
    });
  });

  // 3. Chọn Loại Camera
  document.querySelectorAll('.cam-type-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const camType = btn.getAttribute('data-camtype');
      setCameraType(camType);
    });
  });

  // 4. Chọn Chế Độ Di Chuyển Camera
  document.querySelectorAll('.cam-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const mode = btn.getAttribute('data-mode');
      setCameraMode(mode);
    });
  });

  // 5. Toggle Auto Sway
  const toggleAutoSway = document.getElementById('toggle-auto-sway');
  if (toggleAutoSway) {
    toggleAutoSway.addEventListener('change', (e) => {
      state.isAutoSway = e.target.checked;
    });
  }

  // 6. Wheel Zoom trên Canvas
  if (state.canvas) {
    state.canvas.addEventListener(
      'wheel',
      (e) => {
        if (state.currentCameraMode === 'parallax') {
          e.preventDefault();
          const delta = e.deltaY * -0.0015;
          const newZoom = THREE.MathUtils.clamp(state.manualCamZoom + delta, 0.4, 2.8);
          setCameraZoom(newZoom);
        }
      },
      { passive: false }
    );
  }

  // 7. Sliders điều khiển Camera
  const sliderCamZoom = document.getElementById('slider-cam-zoom');
  if (sliderCamZoom) {
    sliderCamZoom.addEventListener('input', (e) => {
      setCameraZoom(parseFloat(e.target.value));
    });
  }

  const sliderCamHeight = document.getElementById('slider-cam-height');
  if (sliderCamHeight) {
    sliderCamHeight.addEventListener('input', (e) => {
      setCameraHeight(parseFloat(e.target.value));
    });
  }

  const sliderCamPanX = document.getElementById('slider-cam-pan-x');
  if (sliderCamPanX) {
    sliderCamPanX.addEventListener('input', (e) => {
      setCameraPanX(parseFloat(e.target.value));
    });
  }

  // Camera Presets
  document.querySelectorAll('.preset-chip[data-cam-preset]').forEach((chip) => {
    chip.addEventListener('click', () => {
      const preset = chip.getAttribute('data-cam-preset');
      applyCameraPreset(preset);
    });
  });

  const btnResetCamManual = document.getElementById('btn-reset-cam-manual');
  if (btnResetCamManual) {
    btnResetCamManual.addEventListener('click', () => {
      setCameraZoom(1.0);
      setCameraHeight(2.8);
      setCameraPanX(0.0);
    });
  }

  // Reset Camera Trọng Tâm
  const btnResetCam = document.getElementById('btn-reset-cam');
  if (btnResetCam) {
    btnResetCam.addEventListener('click', () => {
      const activeCam = getActiveCamera();
      state.mouse.targetX = 0;
      state.mouse.targetY = 0;
      state.mouse.x = 0;
      state.mouse.y = 0;

      if (state.currentCameraMode === 'orbit' && state.controls) {
        state.controls.target.set(0, 4.2 + state.currentFloorHeight * 0.5, 0);
        activeCam.position.set(0, 4.6 + state.currentFloorHeight * 0.5, 16.0);
      } else {
        activeCam.position.set(state.manualCamPanX, state.manualCamHeight, 16.0);
        activeCam.lookAt(state.manualCamPanX * 0.28, state.manualCamHeight - 1.1 + state.currentFloorHeight * 0.5, 0);
      }
    });
  }

  // 8. Mặt Sàn Vỉa Hè
  document.querySelectorAll('.pavement-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const paveType = btn.getAttribute('data-pavement');
      applyPavement(paveType);
    });
  });

  const sliderPaveRepeat = document.getElementById('slider-pave-repeat');
  if (sliderPaveRepeat) {
    sliderPaveRepeat.addEventListener('input', (e) => {
      const val = parseInt(e.target.value, 10);
      updatePavementRepeat(val);
    });
  }

  // Cao độ Mặt Sàn (Floor Height)
  const sliderFloorHeight = document.getElementById('slider-floor-height');
  if (sliderFloorHeight) {
    sliderFloorHeight.addEventListener('input', (e) => {
      setFloorHeight(parseFloat(e.target.value));
    });
  }

  document.querySelectorAll('.preset-chip[data-floor-y]').forEach((chip) => {
    chip.addEventListener('click', () => {
      const val = parseFloat(chip.getAttribute('data-floor-y'));
      setFloorHeight(val);
    });
  });

  const btnResetFloorHeight = document.getElementById('btn-reset-floor-height');
  if (btnResetFloorHeight) {
    btnResetFloorHeight.addEventListener('click', () => {
      setFloorHeight(0.0);
    });
  }

  // 9. Slider Z-Spacing
  const sliderZ = document.getElementById('slider-z-spacing');
  if (sliderZ) {
    sliderZ.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      updateZSpacing(val);
    });
  }

  document.querySelectorAll('.preset-chip[data-z]').forEach((chip) => {
    chip.addEventListener('click', () => {
      const val = parseFloat(chip.getAttribute('data-z'));
      if (sliderZ) sliderZ.value = val;
      updateZSpacing(val);
    });
  });

  // 10. Layer Visibility
  const bindLayerToggle = (id, callback) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('change', callback);
  };

  bindLayerToggle('layer-fg', (e) => {
    if (state.fgMesh) state.fgMesh.visible = e.target.checked;
    if (state.lanternPointLight) state.lanternPointLight.visible = e.target.checked;
  });
  bindLayerToggle('layer-avatar', (e) => {
    if (state.currentAvatar && state.currentAvatar.root) {
      state.currentAvatar.root.visible = e.target.checked;
    }
  });
  bindLayerToggle('layer-props', (e) => {
    if (state.propsGroup) state.propsGroup.visible = e.target.checked;
  });
  bindLayerToggle('layer-ground', (e) => {
    if (state.groundGroup) state.groundGroup.visible = e.target.checked;
  });
  bindLayerToggle('layer-contact-shadow', (e) => {
    if (state.contactShadowGroup) state.contactShadowGroup.visible = e.target.checked;
  });
  bindLayerToggle('layer-modular', (e) => {
    if (state.modularTownGroup) state.modularTownGroup.visible = e.target.checked;
  });
  bindLayerToggle('layer-bg', (e) => {
    if (state.bgMesh) state.bgMesh.visible = e.target.checked;
  });

  // 11. Avatar & Animation Selector
  const selectAvatar = document.getElementById('select-avatar');
  if (selectAvatar) {
    selectAvatar.addEventListener('change', (e) => {
      state.currentAvatarId = e.target.value;
      spawnAvatar(state.currentAvatarId, state.currentAnimName);
    });
  }

  const selectAnim = document.getElementById('select-anim');
  if (selectAnim) {
    selectAnim.addEventListener('change', (e) => {
      state.currentAnimName = e.target.value;
      if (state.currentAvatar && state.currentAvatar.animator) {
        state.currentAvatar.animator.play(state.currentAnimName);
      }
    });
  }

  // 12. Dock Open / Close
  const dock = document.getElementById('control-dock');
  const btnDockClose = document.getElementById('btn-dock-close');
  const btnDockOpen = document.getElementById('btn-dock-open');
  if (btnDockClose && dock) {
    btnDockClose.addEventListener('click', () => dock.classList.add('collapsed'));
  }
  if (btnDockOpen && dock) {
    btnDockOpen.addEventListener('click', () => dock.classList.remove('collapsed'));
  }

  // 13. Snapshot PNG
  const btnSnapshot = document.getElementById('btn-capture-snapshot');
  if (btnSnapshot) {
    btnSnapshot.addEventListener('click', () => {
      const activeCam = getActiveCamera();
      if (state.renderer && state.scene) {
        state.renderer.render(state.scene, activeCam);
        const dataUrl = state.renderer.domElement.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `multiplane-2.5d-${state.currentStyleKey}-${state.currentCameraType}-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
      }
    });
  }

  // 14. Fullscreen
  const btnFullscreen = document.getElementById('btn-fullscreen');
  if (btnFullscreen) {
    btnFullscreen.addEventListener('click', () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      } else {
        document.exitFullscreen().catch(() => {});
      }
    });
  }

  // 15. Window Resize Handler & Dock Layout
  window.addEventListener('resize', () => {
    const newAspect = window.innerWidth / window.innerHeight;

    if (state.perspCamera) {
      state.perspCamera.aspect = newAspect;
      state.perspCamera.updateProjectionMatrix();
    }

    if (state.orthoCamera) {
      const newFrustumWidth = state.frustumHeight * newAspect;
      const shiftY = state.frameShiftY || 1.6;
      state.orthoCamera.left = -newFrustumWidth / 2;
      state.orthoCamera.right = newFrustumWidth / 2;
      state.orthoCamera.top = state.frustumHeight / 2 + shiftY;
      state.orthoCamera.bottom = -state.frustumHeight / 2 + shiftY;
      state.orthoCamera.updateProjectionMatrix();
    }

    if (state.renderer) {
      state.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    updateDockLayout();
  });

  // Khởi tạo vị trí dock dưới header
  updateDockLayout();
  if (typeof ResizeObserver !== 'undefined') {
    const headerEl = document.querySelector('header');
    if (headerEl) new ResizeObserver(updateDockLayout).observe(headerEl);
  }

  // 16. Project Explorer UI Bindings
  const btnOpenExp = document.getElementById('btn-open-explorer');
  if (btnOpenExp) btnOpenExp.addEventListener('click', openExplorerModal);

  initRecoveryKeyUi();
  updateRecoveryUi();

  const btnHeaderSave = document.getElementById('btn-header-save');
  if (btnHeaderSave) btnHeaderSave.addEventListener('click', openSaveModal);

  const btnCloseExp = document.getElementById('btn-close-explorer');
  if (btnCloseExp) btnCloseExp.addEventListener('click', closeExplorerModal);

  const btnExpNew = document.getElementById('btn-explorer-new');
  if (btnExpNew) btnExpNew.addEventListener('click', resetToDefaultScene);

  const btnExpSave = document.getElementById('btn-explorer-save-current');
  if (btnExpSave) btnExpSave.addEventListener('click', openSaveModal);

  const modalExp = document.getElementById('modal-project-explorer');
  if (modalExp) {
    modalExp.addEventListener('click', (e) => {
      if (e.target.id === 'modal-project-explorer') closeExplorerModal();
    });
  }

  const searchInput = document.getElementById('project-search-input');
  const clearSearchBtn = document.getElementById('btn-clear-search');
  if (searchInput && clearSearchBtn) {
    searchInput.addEventListener('input', (e) => {
      state.currentSearchKeyword = e.target.value;
      clearSearchBtn.style.display = state.currentSearchKeyword ? 'block' : 'none';
      renderProjectGrid();
    });
    clearSearchBtn.addEventListener('click', () => {
      searchInput.value = '';
      state.currentSearchKeyword = '';
      clearSearchBtn.style.display = 'none';
      renderProjectGrid();
      searchInput.focus();
    });
  }

  document.querySelectorAll('#project-filter-pills .filter-pill').forEach((pill) => {
    pill.addEventListener('click', () => {
      document.querySelectorAll('#project-filter-pills .filter-pill').forEach((p) => p.classList.remove('active'));
      pill.classList.add('active');
      state.currentProjectFilter = pill.getAttribute('data-filter') || 'all';
      renderProjectGrid();
    });
  });

  // Save Modal
  const btnCloseSave = document.getElementById('btn-close-save-dialog');
  if (btnCloseSave) btnCloseSave.addEventListener('click', closeSaveModal);

  const btnCancelSave = document.getElementById('btn-cancel-save');
  if (btnCancelSave) btnCancelSave.addEventListener('click', closeSaveModal);

  const btnConfirmSave = document.getElementById('btn-confirm-save');
  if (btnConfirmSave) btnConfirmSave.addEventListener('click', confirmSaveCurrentScene);

  const modalSave = document.getElementById('modal-save-scene');
  if (modalSave) {
    modalSave.addEventListener('click', (e) => {
      if (e.target.id === 'modal-save-scene') closeSaveModal();
    });
  }

  const inputSaveName = document.getElementById('input-save-name');
  const inputSaveFilename = document.getElementById('input-save-filename');
  if (inputSaveFilename) {
    inputSaveFilename.addEventListener('input', () => {
      state.isUserEditingFilename = true;
    });
  }
  if (inputSaveName && inputSaveFilename) {
    inputSaveName.addEventListener('input', () => {
      if (!state.isUserEditingFilename) {
        inputSaveFilename.value = slugify(inputSaveName.value);
      }
    });
  }

  const btnRecapture = document.getElementById('btn-recapture-thumb');
  if (btnRecapture) {
    btnRecapture.addEventListener('click', () => {
      state.currentCapturedThumb = captureSceneThumbnail();
      const thumbImg = document.getElementById('save-snapshot-img');
      if (thumbImg) thumbImg.src = state.currentCapturedThumb;
      showToast('📸 Đã chụp lại khung hình canvas!', 'success');
    });
  }

  // Delete Modal
  const btnCloseDel = document.getElementById('btn-close-delete-dialog');
  if (btnCloseDel) btnCloseDel.addEventListener('click', closeDeleteConfirm);

  const btnCancelDel = document.getElementById('btn-cancel-delete');
  if (btnCancelDel) btnCancelDel.addEventListener('click', closeDeleteConfirm);

  const btnConfirmDel = document.getElementById('btn-confirm-delete-action');
  if (btnConfirmDel) btnConfirmDel.addEventListener('click', executeDeleteProject);

  const modalDel = document.getElementById('modal-confirm-delete');
  if (modalDel) {
    modalDel.addEventListener('click', (e) => {
      if (e.target.id === 'modal-confirm-delete') closeDeleteConfirm();
    });
  }

  // Keyboard Shortcuts
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'o' || e.key === 'O')) {
      e.preventDefault();
      closeSaveModal();
      closeDeleteConfirm();
      toggleExplorerModal();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      closeExplorerModal();
      closeDeleteConfirm();
      openSaveModal();
      return;
    }

    if (e.key === 'Escape') {
      const deleteModal = document.getElementById('modal-confirm-delete');
      if (deleteModal && deleteModal.classList.contains('active')) {
        closeDeleteConfirm();
        return;
      }
      const saveModal = document.getElementById('modal-save-scene');
      if (saveModal && saveModal.classList.contains('active')) {
        closeSaveModal();
        return;
      }
      const explorerModal = document.getElementById('modal-project-explorer');
      if (explorerModal && explorerModal.classList.contains('active')) {
        closeExplorerModal();
      }
    }
  });
}

/**
 * Cập nhật vị trí bắt đầu của Control Dock luôn nằm dưới Header tối thiểu 8px
 */
export function updateDockLayout() {
  const header = document.querySelector('header');
  const dock = document.getElementById('control-dock');
  if (!header || !dock) return;
  const headerBottom = header.getBoundingClientRect().bottom;
  const targetTop = Math.max(76, Math.ceil(headerBottom + 8));
  dock.style.top = `${targetTop}px`;
  document.documentElement.style.setProperty('--dock-top', `${targetTop}px`);
}
